import { useTranslation } from "react-i18next";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import listHandler from "@serie3/common/Domain/listHandler";
import { faTrash, faPen, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";

const STATUS_STYLES = {
  scheduled:  { bg: "rgba(3,142,220,0.10)",   color: "#038edc" },
  published:  { bg: "rgba(81,210,140,0.15)",   color: "#1a7a4a" },
  failed:     { bg: "rgba(243,78,78,0.10)",    color: "#f34e4e" },
  pending:    { bg: "rgba(247,204,83,0.15)",   color: "#a07800" },
};

const APPROVAL_STYLES = {
  proposed:  { bg: "rgba(247,204,83,0.18)",  color: "#8a5a00", label: "waiting for approval" },
  approved:  { bg: "rgba(15,123,134,0.12)",  color: "#0b5f68", label: "approved" },
  rejected:  { bg: "rgba(243,78,78,0.10)",   color: "#b8402d", label: "rejected" },
  cancelled: { bg: "#f5f6f8",                color: "#74788d", label: "cancelled" },
};

/** Who publishes a delegated schedule, plus where its approval stands. */
const PublishAsBadge = ({ schedule }) => {
  if (!schedule.publish_as) return null;
  const a = APPROVAL_STYLES[schedule.approval_status] ?? APPROVAL_STYLES.cancelled;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
      <span style={{ fontSize: 11, color: "#74788d" }} title={schedule.approval_comment || undefined}>
        on account of <strong style={{ color: "#1a2236" }}>{schedule.publish_as.name || schedule.publish_as.username}</strong>
      </span>
      <span style={{
        alignSelf: "flex-start", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
        background: a.bg, color: a.color, letterSpacing: "0.3px",
      }}>
        {a.label}
      </span>
    </div>
  );
};

const PLATFORM_STYLES = {
  linkedin: { bg: "rgba(0,119,181,0.10)", color: "#0077b5" },
  youtube: { bg: "rgba(255,0,0,0.08)", color: "#cc0000" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] ?? { bg: "#f5f6f8", color: "#74788d" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
      background: s.bg, color: s.color, letterSpacing: "0.3px", textTransform: "capitalize",
    }}>
      {status ?? "—"}
    </span>
  );
};

const PlatformBadge = ({ platforms, platform }) => {
  const list = platforms ?? (platform ? [platform] : []);
  const display = list.length === 0 ? ["linkedin"] : list;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {display.map((p) => {
        const s = PLATFORM_STYLES[p?.toLowerCase()] ?? { bg: "#f5f6f8", color: "#74788d" };
        return (
          <span key={p} style={{
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
            background: s.bg, color: s.color, letterSpacing: "0.3px", textTransform: "capitalize",
          }}>
            {p}
          </span>
        );
      })}
    </div>
  );
};

const IconBtn = ({ icon, onClick, color, title, href }) => {
  const shared = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 36, height: 36, borderRadius: 8, border: "none", cursor: "pointer",
    background: color === "#f34e4e" ? "rgba(243,78,78,0.10)" : "rgba(3,142,220,0.10)",
    color,
    transition: "background 0.15s ease",
    textDecoration: "none",
  };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={shared} title={title}>
        <FontAwesomeIcon icon={icon} style={{ fontSize: 14 }} />
      </a>
    );
  }
  return (
    <button onClick={onClick} style={shared} title={title}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.75"; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 14 }} />
    </button>
  );
};

const ScheduleTable = ({ dataList, setDataList, setEditData, setshowPlanningForm, schedules, setSchedules, onDeleted = null }) => {
  const { t } = useTranslation();

  const deleteSchedule = (index, scheduleId) => {
    listHandler
      .deleteFromList(index, crudApi.del(SocialEndpoints.posts.schedule.delete, scheduleId), setDataList, dataList)
      .then(() => {
        setSchedules(schedules.filter((s) => s.id !== scheduleId));
        // the post's status may have changed (last plan gone = back to ready)
        if (typeof onDeleted === "function") onDeleted(scheduleId);
      })
      .catch((error) => console.error("Error deleting schedule:", error));
  };

  if (!dataList || dataList.length === 0) {
    return (
      <div style={{
        border: "2px dashed #e0e4ea", borderRadius: 12, padding: "32px 24px",
        textAlign: "center", color: "#adb5bd", fontSize: 13,
      }}>
        {t("Backend.Post.Schedule.NoSchedules", "No schedules yet")}
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="data-table table-striped">
        <thead>
          <tr>
            <th>{t("Backend.Post.Schedule.Date")}</th>
            <th>{t("Translation.Timezone")}</th>
            <th>{t("Backend.Post.Schedule.Platform")}</th>
            <th>{t("Backend.Post.Schedule.Status")}</th>
            <th style={{ textAlign: "right" }}></th>
          </tr>
        </thead>
        <tbody>
          {dataList.map((schedule, index) => (
            <tr key={schedule.id ?? index}>
              <td style={{ fontWeight: 500, color: "#1a2236", whiteSpace: "nowrap" }}>
                {formatDate(schedule.local_user_time)}
              </td>
              <td style={{ fontSize: 12, color: "#74788d" }}>
                {schedule.timezone}
              </td>
              <td>
                <PlatformBadge platforms={schedule.platforms} platform={schedule.platform} />
              </td>
              <td>
                <StatusBadge status={schedule.post_status} />
                <PublishAsBadge schedule={schedule} />
              </td>
              <td>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  {schedule.portal_post_id && (
                    <IconBtn
                      icon={faArrowUpRightFromSquare}
                      href={`https://www.linkedin.com/feed/update/${schedule.portal_post_id}`}
                      color="#038edc"
                      title="View on LinkedIn"
                    />
                  )}
                  <IconBtn
                    icon={faPen}
                    color="#038edc"
                    title={t("Backend.Buttons.Edit")}
                    onClick={() => { setEditData(schedule); setshowPlanningForm(true); }}
                  />
                  <IconBtn
                    icon={faTrash}
                    color="#f34e4e"
                    title={t("Backend.Buttons.Delete")}
                    onClick={() => deleteSchedule(index, schedule.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ScheduleTable;
